import { useState, useEffect } from 'react';

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const videos = [
    {
      id: 'w0irWq2O-Ag',
      url: 'https://www.youtube.com/watch?v=w0irWq2O-Ag',
      thumbnail: 'https://img.youtube.com/vi/w0irWq2O-Ag/maxresdefault.jpg',
      title: 'Creative Journeys Learning Center Experience'
    },
    {
      id: 'pH4mJfjZTqw',
      url: 'https://www.youtube.com/watch?v=pH4mJfjZTqw',
      thumbnail: 'https://img.youtube.com/vi/pH4mJfjZTqw/maxresdefault.jpg',
      title: 'A Day at Creative Journeys'
    },
    {
      id: 'xRCg4gng5ig',
      url: 'https://www.youtube.com/watch?v=xRCg4gng5ig',
      thumbnail: 'https://img.youtube.com/vi/xRCg4gng5ig/maxresdefault.jpg',
      title: 'Parent Testimonials & Stories'
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedVideo) {
        if (e.key === 'Escape') {
          setSelectedVideo(null);
        }
      } else {
        if (e.key === 'ArrowLeft') {
          handlePrev();
        } else if (e.key === 'ArrowRight') {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedVideo, currentIndex]);

  const getVisibleCount = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? videos.length - getVisibleCount() : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= videos.length - getVisibleCount() ? 0 : prev + 1));
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  const openVideo = (videoId: string) => {
    setSelectedVideo(videoId);
  };

  const closeModal = () => {
    setSelectedVideo(null);
  };

  const truncateTitle = (title: string, maxLength: number = 50) => {
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  const visibleCount = getVisibleCount();
  const maxDots = Math.max(1, videos.length - visibleCount + 1);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#2E5939] mb-6"
            style={{ fontFamily: '"Poppins", sans-serif' }}
          >
            Family Testimonials
          </h2>
          <p 
            className="text-xl text-[#2B2B2B]/80 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Real experiences from Creative Journeys families.
          </p>
        </div>

        <div className="relative">
          {/* Video Carousel */}
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ 
                transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
                touchAction: 'pan-y'
              }}
            >
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="flex-shrink-0 px-4"
                  style={{ width: `${100 / visibleCount}%` }}
                >
                  <div 
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    onClick={() => openVideo(video.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openVideo(video.id);
                      }
                    }}
                    aria-label={`Play video: ${video.title}`}
                  >
                    <div className="relative aspect-video">
                      <img 
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                        <div className="w-16 h-16 bg-[#2E5939] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                          <i className="ri-play-fill text-white text-3xl ml-1"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 
                        className="text-lg font-semibold text-[#2E5939]"
                        style={{ fontFamily: '"Poppins", sans-serif' }}
                      >
                        {truncateTitle(video.title)}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {visibleCount < videos.length && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#2E5939] hover:text-white transition-all duration-300 z-10 cursor-pointer"
                aria-label="Previous video"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#2E5939] hover:text-white transition-all duration-300 z-10 cursor-pointer"
                aria-label="Next video"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            </>
          )}

          {/* Dot Indicators */}
          {maxDots > 1 && (
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: maxDots }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    index === currentIndex 
                      ? 'bg-[#2E5939] scale-125' 
                      : 'bg-[#2E5939]/30 hover:bg-[#2E5939]/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-10 cursor-pointer"
              aria-label="Close video"
            >
              <i className="ri-close-line text-2xl text-[#2E5939]"></i>
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}
      
      {/* Soft Section Divider */}
      <div className="mt-20">
        <div className="h-px bg-gradient-to-r from-transparent via-[#2E5939]/20 to-transparent"></div>
      </div>
    </section>
  );
}